import {
  type AIAgent,
  type AIConnector,
  getConnectorId,
} from "@p0security/shared/types/ai-inventory";
import { useMemo } from "react";

import { indexServers, matchConnectorToServer } from "../graph/matchServer.ts";
import type {
  AgentGraphData,
  ConnectorWithContext,
  P0MCPContext,
  PolicySummary,
  Risk,
  RiskSeverity,
} from "../graph/types.ts";
import { isConnectorManaged } from "../platforms/enrollment.ts";
import { hashNumber } from "./hash.ts";
import type { ManagedMcpServer } from "./useManagedMcpServers.ts";

type RiskTemplate = {
  title: string;
  description: string;
  severity: RiskSeverity;
};

type ServerKind = "generic" | "github" | "p0" | "postgres" | "salesforce";

const KEYWORD_KINDS: ReadonlyArray<
  readonly [Exclude<ServerKind, "generic">, string]
> = [
  ["p0", "p0"],
  ["github", "github"],
  ["salesforce", "salesforce"],
  ["postgres", "postgres"],
];

const RISK_POOLS: Record<ServerKind, readonly RiskTemplate[]> = {
  p0: [],
  github: [
    {
      severity: "urgent",
      title: "Write to protected branches",
      description:
        "Token scope allows push to main and release branches without review.",
    },
    {
      severity: "high",
      title: "Classic PAT without expiry",
      description: "Long-lived token; compromise yields indefinite access.",
    },
    {
      severity: "high",
      title: "Org-wide repo scope",
      description:
        "Token covers every repo in the org, not just those this agent uses.",
    },
    {
      severity: "medium",
      title: "Workflow write access",
      description:
        "Can edit .github/workflows — supply-chain risk via CI injection.",
    },
    {
      severity: "low",
      title: "Secrets surface via code search",
      description:
        "Search tool returns file contents that may include secrets.",
    },
  ],
  salesforce: [
    {
      severity: "high",
      title: "System Administrator profile",
      description: "Connector user has full CRUD on every object.",
    },
    {
      severity: "high",
      title: "Refresh token without expiry",
      description:
        "Connected app refresh token doesn't rotate; revocation is manual.",
    },
    {
      severity: "medium",
      title: "API access to regulated fields",
      description: "Query API can read PII/PHI fields (SSN, DOB, payment).",
    },
  ],
  postgres: [],
  generic: [
    {
      severity: "high",
      title: "Overbroad scope",
      description: "Tool grants write access beyond what the agent needs.",
    },
    {
      severity: "medium",
      title: "Shared client credential",
      description: "Client secret reused across multiple agents.",
    },
    {
      severity: "low",
      title: "Read-all data access",
      description: "Connector can read all documents, not just the user's.",
    },
  ],
};

const detectKind = (server: ManagedMcpServer): ServerKind => {
  const haystack = `${server.label ?? ""} ${server.id}`.toLowerCase();
  for (const [kind, keyword] of KEYWORD_KINDS) {
    if (haystack.includes(keyword)) return kind;
  }
  return "generic";
};

export const generateServerRisks = (server: ManagedMcpServer): Risk[] => {
  const pool = RISK_POOLS[detectKind(server)];
  if (pool.length === 0) return [];
  const base = server.id;
  const count = (hashNumber(base, 3) % 2) + 1;
  const start = hashNumber(base, 5) % pool.length;
  return Array.from({ length: Math.min(count, pool.length) }, (_, i) => {
    const template = pool[(start + i) % pool.length];
    return {
      id: `${base}-risk-${i}`,
      severity: template.severity,
      title: template.title,
      description: template.description,
      connectorId: base,
    };
  });
};

export const useAgentGraphData = (
  agent: AIAgent | undefined,
  connectors: AIConnector[],
  policies: readonly PolicySummary[] = [],
  mcpServers: readonly ManagedMcpServer[] = [],
  /**
   * Server IDs to treat as connected regardless of connector-derived
   * matching. Lets log-observed traffic populate the graph for agents
   * (e.g. local-agents) that don't expose first-class connector records.
   */
  additionalServerIds: ReadonlySet<string> = new Set()
): AgentGraphData | undefined =>
  useMemo(() => {
    if (!agent) return undefined;
    const withContext: ConnectorWithContext[] = connectors.map((connector) => {
      const managed = isConnectorManaged(connector);
      const connectorId = getConnectorId(connector);
      // Unmanaged connectors bypass the P0 gateway. That implicates two
      // governance/audit policies: no centralized audit trail, and no
      // enforced agent registration before it reaches the connector. Each
      // risk carries its OWASP ASI + NIST AI RMF refs so the UI can show
      // a hover popover with the standards mapping.
      const risks: Risk[] = managed
        ? []
        : [
            {
              id: `${connectorId}-no-audit-trail`,
              severity: "high",
              connectorId,
              title:
                "All agent sessions must produce a full audit trail — tool call, policy fired, identity, timestamp",
              description:
                "Sessions through this unmanaged connector bypass the P0 gateway, so tool calls, policy decisions, identity, and timestamps are not centrally logged.",
              standards: {
                owaspAsi: ["ASI01", "ASI02", "ASI03"],
                nistAiRmf: ["MEASURE 2.8", "GOVERN 1.4"],
              },
            },
            {
              id: `${connectorId}-unregistered-agent`,
              severity: "high",
              connectorId,
              title:
                "Every agent must be registered before accessing company systems",
              description:
                "Because this connector bypasses the P0 gateway, the agent reaching it isn't validated against the registered-client list — an unregistered or rogue agent can call it directly.",
              standards: {
                owaspAsi: ["ASI10"],
                nistAiRmf: ["GOVERN 1.2", "MAP 1.1"],
              },
            },
          ];
      return { connector, managed, risks, tools: [] };
    });
    const isP0Server = (s: ManagedMcpServer): boolean => {
      const haystack = `${s.id} ${s.label} ${s.path}`.toLowerCase();
      return /\bp0\b/.test(haystack) || haystack.includes("p0mcp");
    };
    const indexed = indexServers(mcpServers);
    // The keyword match returns the first hit, so without excluding the P0
    // server a connector id like `...p0-20github-20mcp-20server...` would
    // match "p0" and miss "github". P0 is always added separately below.
    const integrationIndexed = indexed.filter(
      ({ server }) => !isP0Server(server)
    );
    const hasManagedConnector = withContext.some((c) => c.managed);
    const connectedIds = new Set<string>();
    if (hasManagedConnector) {
      for (const s of mcpServers) {
        if (isP0Server(s)) connectedIds.add(s.id);
      }
    }
    for (const c of withContext) {
      if (!c.managed) continue;
      const match = matchConnectorToServer(c.connector, integrationIndexed);
      if (match) connectedIds.add(match.id);
    }
    for (const id of additionalServerIds) connectedIds.add(id);
    // Connector-less agents (e.g. local-agents) seed connectedIds purely
    // from observed traffic, in which case the P0 gateway should still
    // appear so the graph reflects the brokered topology.
    if (additionalServerIds.size > 0) {
      for (const s of mcpServers) {
        if (isP0Server(s)) connectedIds.add(s.id);
      }
    }
    const connectedServers = mcpServers.filter((s) => connectedIds.has(s.id));
    const serverRisks: Record<string, Risk[]> = {};
    for (const s of connectedServers) {
      serverRisks[s.id] = generateServerRisks(s);
    }
    const enforcingCount = connectedServers.length;
    const p0Mcp: P0MCPContext | null =
      enforcingCount === 0
        ? null
        : {
            enforcingCount,
            policies,
            managedServers: connectedServers,
          };
    return {
      agent,
      p0Mcp,
      connectors: withContext,
      mcpServers: connectedServers,
      serverRisks,
    };
  }, [agent, connectors, policies, mcpServers, additionalServerIds]);
